import { useTranslation } from 'react-i18next'
import { useGoogleLocationQuery } from '@/features/google-location'
import { GOOGLE_LOCATION_ANCHOR, useSettingsModal } from '@/features/settings'
import Button from '@/shared/components/Button/Button'
import Skeleton from '@/shared/components/Skeleton/Skeleton'
import styles from './ReviewDestination.module.scss'

interface ReviewDestinationProps {
  disabled?: boolean
}

/**
 * Where a guest is sent to review the venue: its place on Google, connected
 * in Settings, and the same for every guest whatever stars they gave.
 */
function ReviewDestination({ disabled }: ReviewDestinationProps) {
  const { t } = useTranslation()
  const location = useGoogleLocationQuery()
  const openSettings = useSettingsModal(s => s.open)
  const reviewUrl =
    location.data?.status === 'active' ? location.data.reviewUrl : null

  return (
    <div className={styles.destination}>
      <p className={`label-bold-medium ${styles.label}`}>
        {t('FEEDBACK.INVITE.PLACE')}
      </p>
      {/* Without a place the page's warning has already said so. */}
      {reviewUrl && (
        <p className={`body-small ${styles.hint}`}>
          {t('FEEDBACK.INVITE.PLACE_CONNECTED')}
        </p>
      )}
      {location.isPending && <Skeleton width="10rem" height="2rem" />}
      {reviewUrl && (
        // So the owner can see where the link lands before any guest does.
        <Button
          label={t('FEEDBACK.INVITE.PLACE_TEST')}
          color="primary"
          variant="text"
          smallFont
          disabled={disabled}
          icon="las la-external-link-alt icon-medium"
          onClick={() =>
            window.open(reviewUrl, '_blank', 'noopener,noreferrer')
          }
        />
      )}
      {/*
        Only once the answer is in: while it loads, or when it failed, a
        connected venue would be told to connect.
      */}
      {location.isSuccess && !reviewUrl && (
        <Button
          label={
            location.data?.status === 'needs_reselect'
              ? t('GOOGLE_LOCATION.RESELECT')
              : t('FEEDBACK.INVITE.PLACE_CONNECT')
          }
          color="primary"
          variant="text"
          smallFont
          icon="las la-map-marker-alt icon-medium"
          onClick={() => openSettings('restaurant', GOOGLE_LOCATION_ANCHOR)}
        />
      )}
    </div>
  )
}

export default ReviewDestination
